<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { addBreadcrumb } from '@/telemetry/sentry';
import { useEntrBoot } from './app/useEntrBoot';
import { createEntrLocaleController } from './i18n/entrLocaleController';
import AttractScreen from './screens/Attract/AttractScreen.vue';
import CardPaymentScreen from './screens/CardPayment/CardPaymentScreen.vue';
import FarewellScreen from './screens/Farewell/FarewellScreen.vue';
import PhotoSelectionScreen from './screens/PhotoSelection/PhotoSelectionScreen.vue';
import PrintingScreen from './screens/Printing/PrintingScreen.vue';
import ScanTicketScreen from './screens/ScanTicket/ScanTicketScreen.vue';
import EntrIdleModal from './ui/components/EntrIdleModal.vue';
import EntrReconnectingBanner from './ui/components/EntrReconnectingBanner.vue';
import EntrResetLogo from './ui/components/EntrResetLogo.vue';
import EntrScreenTransition from './ui/components/EntrScreenTransition.vue';
import EntrSheet from './ui/components/EntrSheet.vue';
import EntrToast from './ui/components/EntrToast.vue';

const localeController = createEntrLocaleController();
const { currentLocale, selectLocale } = localeController;
const { controller, hasFatalRenderError, priceConfig, reloadApp } = useEntrBoot(
  {
    onAttractReset: localeController.resetForNewSession,
  }
);
const {
  screen,
  groups,
  isBusy,
  session,
  selection,
  quote,
  sheet,
  toastKey,
  paymentState,
  printState,
  idlePromptVisible,
  idleSecondsRemaining,
  formattedAmountDue,
  scanner,
  isStarted,
} = controller;

const { t } = useI18n({ useScope: 'global' });
const logoUrl = new URL('./assets/brand/entr-logo.svg', import.meta.url).href;

function tr(key: string, params?: Record<string, string | number>): string {
  return params ? t(key, params) : t(key);
}

function handleResetLogoReset(): void {
  addBreadcrumb('entr.reset', 'Reset triggered via logo tap', {
    screen: screen.value,
    session_id: session.value?.id ?? null,
  });
  void controller.resetToAttract();
}

function handleResetLogoHardReset(): void {
  addBreadcrumb('entr.reset', 'Hard reset triggered via 5x logo tap', {
    screen: screen.value,
    session_id: session.value?.id ?? null,
  });
  void reloadApp();
}
</script>

<template>
  <div class="app-stage">
    <EntrReconnectingBanner />
    <main class="kiosk-frame" data-testid="entr-kiosk">
      <section
        v-if="hasFatalRenderError"
        class="fatal-screen"
        aria-live="assertive"
      >
        <h1>{{ t('fatal.title') }}</h1>
        <p>{{ t('fatal.subtitle') }}</p>
        <button type="button" @click="reloadApp">
          {{ t('fatal.reload') }}
        </button>
      </section>

      <template v-else>
        <EntrResetLogo
          label="Reset"
          @reset="handleResetLogoReset"
          @hard-reset="handleResetLogoHardReset"
        >
          <img class="reset-logo-mark" :src="logoUrl" alt="" />
        </EntrResetLogo>

        <div class="language-pill" aria-label="Language">
          <button
            type="button"
            class="language-option language-option-nl"
            aria-label="Nederlands"
            :aria-pressed="currentLocale === 'nl'"
            :class="{ 'is-active': currentLocale === 'nl' }"
            @click="selectLocale('nl')"
          >
            {{ t('language.nl') }}
          </button>
          <span class="language-separator" aria-hidden="true">|</span>
          <button
            type="button"
            class="language-option language-option-en"
            aria-label="English"
            :aria-pressed="currentLocale === 'en'"
            :class="{ 'is-active': currentLocale === 'en' }"
            @click="selectLocale('en')"
          >
            {{ t('language.en') }}
          </button>
        </div>

        <EntrScreenTransition>
          <AttractScreen
            v-if="screen === 'attract'"
            key="attract"
            :groups="groups"
            :can-open-scan="isStarted"
            @select-group="controller.selectGroup"
            @open-scan="controller.openScan"
            @help="controller.callHelp"
          />

        <ScanTicketScreen
          v-else-if="screen === 'scan-ticket'"
          key="scan-ticket"
          :is-busy="isBusy"
          :scanner="scanner"
          @back="controller.goBack"
          @help="controller.callHelp"
          @scan-ticket="controller.handleTicketCode"
        />

        <PhotoSelectionScreen
          v-else-if="screen === 'select-photos' && session"
          key="select-photos"
          :session="session"
          :selection="selection"
          :quote="quote"
          :price-config="priceConfig"
          @change-quantity="controller.changeQuantity"
          @continue="controller.continueFromSelection"
          @back="controller.goBack"
          @help="controller.callHelp"
        />

        <CardPaymentScreen
          v-else-if="screen === 'card-payment'"
          key="card-payment"
          :amount-label="formattedAmountDue"
          :state="paymentState"
          :quote="quote"
          @back="controller.goBack"
          @help="controller.callHelp"
        />

        <PrintingScreen
          v-else-if="screen === 'printing'"
          key="printing"
          :state="printState"
          @help="controller.callHelp"
        />

        <FarewellScreen
          v-else-if="screen === 'farewell'"
          key="farewell"
          :logo-url="logoUrl"
        />
        </EntrScreenTransition>

        <Transition name="entr-sheet" mode="out-in">
          <EntrSheet
            v-if="sheet?.kind === 'error'"
            key="error-sheet"
            :title="tr(sheet.titleKey, sheet.titleParams)"
            :body="tr(sheet.bodyKey, sheet.bodyParams)"
            :retry-label="sheet.retry ? t('common.retry') : undefined"
            :exit-label="sheet.exit ? t('common.exit') : undefined"
            :auto-dismiss-ms="sheet.autoDismissMs"
            @close="controller.closeSheet"
            @retry="sheet.retry?.()"
            @exit="sheet.exit?.()"
          />
        </Transition>

        <Transition name="idle-modal">
          <EntrIdleModal
            v-if="idlePromptVisible"
            :seconds-remaining="idleSecondsRemaining"
            @continue="controller.continueIdleSession"
          />
        </Transition>

        <EntrToast :message="toastKey ? tr(toastKey) : ''" />
      </template>
    </main>
  </div>
</template>

<style scoped>
.fatal-screen {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  gap: 28px;
  padding: 96px;
  text-align: center;
  background: var(--entr-canvas);
  color: var(--entr-text);
}

.fatal-screen h1 {
  margin: 0;
  font-family: var(--entr-font-title);
  font-size: 72px;
  font-weight: 300;
  line-height: 1.05;
}

.fatal-screen p {
  margin: 0;
  color: var(--entr-text-muted);
  font-size: 34px;
}

.fatal-screen button {
  min-width: 260px;
  min-height: 88px;
  justify-self: center;
  border: 0;
  border-radius: 999px;
  background: var(--entr-button-bg);
  color: var(--entr-text);
  font: inherit;
  font-size: 28px;
}

.reset-logo-mark {
  width: 180px;
  height: auto;
  display: block;
}
</style>
